import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import isEmpty from 'lodash/isEmpty';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { addActivity, logout, openContactSupportDrawer } from '@capital-access/common/actions';
import { PendoAccessService } from '@capital-access/common/auth';
import { CookieSettingsService, SelectorSecurity } from '@capital-access/common/components';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import { LocalizationService } from '@capital-access/common/localization';
import { ToggleNavItemConfig, UserProfileDropdownItem } from '@capital-access/common/navigation';
import {
  getPrimarySecuritySettings,
  SecuritySelectorProvider,
  setSelectedSecurityId
} from '@capital-access/common/security';
import {
  getAccountPreferences,
  getEntitlements,
  getUserLoadStatus,
  REPORT_ACCESS_ONLY_PREFERENCE_KEY,
  REPORT_ACCESS_RESTRICTION_FEATURE_TOGGLE
} from '@capital-access/common/user';
import { selectIsImpersonating } from '@capital-access/common/user';
import { CA_CONSULTANT_IMPERSONATION_ENTITLEMENT } from '@capital-access/common/user';
import { mapToBnyReportOnlyAccessRestriction, mapToRecord } from '@capital-access/common/utils';
import { getBrandingSettings } from '@capital-access/theming';
import { EntityItem } from '../../models/entity-item';
import {
  CONTACT_SUPPORT_URL,
  COOKIE_SETTINGS_URL,
  LOGOUT_URL,
  NAVIGATION_LOCALIZATION_SCOPE
} from '../navigation.constants';
import { SharedNavigationItemsService } from '../services/ca-shared-navigation-items.service';

@Component({
  selector: 'ca-top-nav',
  templateUrl: './top-nav.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopNavComponent {
  @Input() flexibleTopNav = false;

  user$ = this.sharedNavigationItemsService.user$;
  userSettingsOptions$: Observable<UserProfileDropdownItem[]> =
    this.sharedNavigationItemsService.userSettingsOptionsTop$;

  showApplicationTitle$ = this.store.select(getBrandingSettings).pipe(map(({ brand }) => !(brand === 'bny')));
  isImpersonating$ = this.store.select(selectIsImpersonating);

  shouldHideComponents$: Observable<boolean>;

  shouldHideFeedbackButton$: Observable<boolean>;

  hasCAConsultantEntitlement$: Observable<boolean>;

  hasConsultantClientAccountSwitcherFT$: Observable<boolean>;

  toggleSettings: ToggleNavItemConfig = this.sharedNavigationItemsService.toggleSettingsOption[0];

  entityIcon: string = this.sharedNavigationItemsService.entitySettingsIcon;
  entityList: EntityItem[] = this.sharedNavigationItemsService.entitySettingsOptionsTop;

  applicationSecurities$ = this.provider.getSecurities().pipe(
    switchMap(availableSecurities =>
      this.store.select(getPrimarySecuritySettings).pipe(
        withLatestFrom(
          this.localizationService.localize(
            ['availableSecurities', 'pinnedSecurities'],
            {},
            NAVIGATION_LOCALIZATION_SCOPE
          )
        ),
        map(([primarySecurities, [availableSecuritiesTitle, pinnedSecuritiesTitle]]) => {
          const all = mapToRecord(availableSecurities, x => x.id);
          const primary = primarySecurities.primary?.filter(id => !!all[id]).map(x => all[x]) ?? [];

          const availableSecurityItems: SelectorSecurity[] = availableSecurities.map(x => {
            return { ...x, category: availableSecuritiesTitle };
          });

          const pinnedSecurityItems: SelectorSecurity[] = primary.map(x => {
            return { ...x, category: pinnedSecuritiesTitle };
          });

          if (isEmpty(pinnedSecurityItems) && availableSecurityItems.length > 5) {
            pinnedSecurityItems.push({
              id: null,
              name: '',
              ticker: '',
              category: pinnedSecuritiesTitle
            } as unknown as SelectorSecurity);
          }

          return availableSecurityItems.length > 5
            ? [...pinnedSecurityItems, ...availableSecurityItems]
            : availableSecurityItems;
        })
      )
    )
  );

  applicationSelectedSecurity$ = this.provider.getSelectedSecurity();

  constructor(
    private router: Router,
    private store: Store,
    private localizationService: LocalizationService,
    private sharedNavigationItemsService: SharedNavigationItemsService,
    private provider: SecuritySelectorProvider,
    private cookieSettingsService: CookieSettingsService,
    private pendoAccessService: PendoAccessService
  ) {
    const userStatus$ = this.store.select(getUserLoadStatus);
    const preferences$ = this.store.select(getAccountPreferences);
    const featureToggle$ = this.store.select(getFeatureToggle(REPORT_ACCESS_RESTRICTION_FEATURE_TOGGLE));

    this.shouldHideComponents$ = combineLatest([userStatus$, preferences$, featureToggle$]).pipe(
      mapToBnyReportOnlyAccessRestriction(REPORT_ACCESS_ONLY_PREFERENCE_KEY)
    );

    this.hasConsultantClientAccountSwitcherFT$ = this.store.select(
      getFeatureToggle('consultant-client-account-switcher-ISD-16483')
    );
    this.hasCAConsultantEntitlement$ = this.store
      .select(getEntitlements)
      .pipe(
        map(
          entitlements =>
            entitlements?.functions.some(f => f.sourceId === CA_CONSULTANT_IMPERSONATION_ENTITLEMENT) ?? false
        )
      );

    this.shouldHideFeedbackButton$ = this.pendoAccessService.checkAccess().pipe(map(result => result.shouldHidePendo));
  }

  onSetSecurities() {
    this.router.navigate(['user', 'settings']);
  }

  onApplicationSecuritySelected(selectedSecurity: { id: number }) {
    this.store.dispatch(setSelectedSecurityId({ selectedSecurityId: selectedSecurity.id }));
  }

  onSurveillanceToggle() {
    this.store.dispatch(this.toggleSettings.action);
  }

  entityItemClick(entityIndex: number): void {
    this.store.dispatch(this.entityList[entityIndex].action);
  }
  userItemClick($event: unknown): void {
    const item = $event as UserProfileDropdownItem;

    if (item.route === CONTACT_SUPPORT_URL) {
      this.store.dispatch(openContactSupportDrawer());
    } else if (item.route === LOGOUT_URL) {
      this.store.dispatch(logout());
    } else if (item.route === COOKIE_SETTINGS_URL) {
      this.cookieSettingsService.showCookieSettings();
    } else {
      this.router.navigate([item.route]);
    }
  }

  onAddActivity(tooltip: NgbTooltip): void {
    if (tooltip.isOpen()) {
      tooltip.close();
    }
    this.store.dispatch(addActivity());
  }

  openPendoPortal(): void {
    this.router.navigate(['pendo-portal']);
  }

  logout() {
    this.store.dispatch(logout());
  }
}
